import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './SuspendedClientsPanel.css';
import SuspendedClientsLogin from './SuspendedClientsLogin';
import { 
  AlertTriangle, 
  User, 
  CreditCard, 
  Phone, 
  Mail, 
  MapPin, 
  Package, 
  DollarSign, 
  FileText, 
  Calendar,
  CheckCircle,
  XCircle,
  Copy,
  Search,
  MessageCircle,
  RefreshCw,
  LogOut,
  Check
} from 'lucide-react';

function SuspendedClientsPanel() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [suspendedClients, setSuspendedClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [paymentModal, setPaymentModal] = useState({ show: false, client: null });
  const [verifyingClient, setVerifyingClient] = useState(null);
  const [contactedClients, setContactedClients] = useState(() => {
    const saved = localStorage.getItem('contactedClients');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    // Verificar si ya tiene sesi?n activa
    const token = localStorage.getItem('suspendedClientsToken');
    const loginTime = localStorage.getItem('suspendedClientsLoginTime');
    
    if (token && loginTime) {
      const timeElapsed = Date.now() - parseInt(loginTime);
      // Sesi?n v?lida por 24 horas
      if (timeElapsed < 86400000) {
        setIsAuthenticated(true);
        loadSuspendedClients();
        const interval = setInterval(loadSuspendedClients, 120000);
        return () => clearInterval(interval);
      } else {
        // Sesi?n expirada
        localStorage.removeItem('suspendedClientsToken');
        localStorage.removeItem('suspendedClientsLoginTime');
      }
    }
    setLoading(false);
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    loadSuspendedClients();
    const interval = setInterval(loadSuspendedClients, 120000);
  };

  const handleLogout = () => {
    localStorage.removeItem('suspendedClientsToken');
    localStorage.removeItem('suspendedClientsLoginTime');
    setIsAuthenticated(false);
    setSuspendedClients([]);
  };

  const loadSuspendedClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/api/mikrotik/suspended-clients');
      
      if (response.data.success) {
        setSuspendedClients(response.data.clients || []);
        setLastUpdate(response.data.lastUpdate || new Date());
      } else {
        setError(response.data.message || 'Error al cargar clientes suspendidos');
      }
    } catch (error) {
      console.error('Error cargando clientes suspendidos:', error);
      setError('Error al conectar con Mikrotik. Verifique la conexi?n.');
    } finally {
      setLoading(false);
    }
  };

  const forceRefresh = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Forzar actualizaci?n desde Mikrowisp
      await axios.post('/api/mikrotik/refresh-suspended-clients');
      
      // Recargar lista actualizada
      await loadSuspendedClients();
    } catch (error) {
      console.error('Error actualizando clientes:', error);
      setError(error.response?.data?.message || 'Error al actualizar');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentClick = (client) => {
    setPaymentModal({ show: true, client: client });
  };

  const confirmPayment = async () => {
    const { client } = paymentModal;
    if (!client) return;

    setProcessingPayment(client.id);
    setPaymentModal({ show: false, client: null });

    try {
      const response = await axios.post('/api/mikrotik/register-payment', {
        idcliente: client.id,
        monto: parseFloat(client.deuda),
        descripcion: `Pago en efectivo - Cliente: ${client.nombre}`
      });

      if (response.data.success) {
        alert(`PAGO REGISTRADO EXITOSAMENTE\n\nCliente: ${client.nombre}\nMonto: RD$${parseFloat(client.deuda).toFixed(2)}\n\n${response.data.message}`);
        
        // Recargar lista de clientes suspendidos
        await loadSuspendedClients();
      } else {
        alert(`ERROR AL REGISTRAR EL PAGO:\n\n${response.data.message}`);
      }
    } catch (error) {
      console.error('Error registrando pago:', error);
      alert(`ERROR AL REGISTRAR EL PAGO:\n\n${error.response?.data?.message || error.message}`);
    } finally {
      setProcessingPayment(null);
    }
  };

  const cancelPayment = () => {
    setPaymentModal({ show: false, client: null });
  };

  const verifyClientStatus = async (client) => {
    setVerifyingClient(client.id);
    try {
      const response = await axios.post('/api/mikrotik/verify-client-status', {
        idcliente: client.id
      });

      if (response.data.success) {
        const currentStatus = response.data.estado.toLowerCase();
        const isSuspended = ['suspendido', 'suspended', 'cortado', 'inactivo', 'inactive', 'desactivado'].includes(currentStatus);

        if (isSuspended) {
          alert(`CLIENTE CONFIRMADO COMO SUSPENDIDO\n\nID: ${client.id}\nNombre: ${client.nombre}\nEstado actual: ${response.data.estado}\nDeuda actual: RD$${parseFloat(response.data.deuda || 0).toFixed(2)}`);
        } else {
          alert(`ATENCI?N: CLIENTE YA NO EST? SUSPENDIDO\n\nID: ${client.id}\nNombre: ${client.nombre}\nEstado actual: ${response.data.estado}\n\nEl cliente ser? removido de la lista.`);
          // Recargar lista
          await loadSuspendedClients();
        }
      } else {
        alert(`Error al verificar cliente:\n${response.data.message}`);
      }
    } catch (error) {
      console.error('Error verificando cliente:', error);
      alert(`Error al verificar estado del cliente:\n${error.response?.data?.message || error.message}`);
    } finally {
      setVerifyingClient(null);
    }
  };

  const openWhatsApp = (phoneNumber) => {
    // Limpiar el n?mero de tel?fono
    let cleanNumber = phoneNumber.replace(/\D/g, '');
    
    // Si el n?mero tiene 10 d?gitos y empieza con 809/829/849, agregar c?digo de pa?s 1
    if (cleanNumber.length === 10 && (cleanNumber.startsWith('809') || cleanNumber.startsWith('829') || cleanNumber.startsWith('849'))) {
      cleanNumber = '1' + cleanNumber;
    }
    
    // Abrir WhatsApp Web
    window.open(`https://wa.me/${cleanNumber}`, '_blank');
  };

  const markAsContacted = (clientId) => {
    const updated = { ...contactedClients, [clientId]: true };
    setContactedClients(updated);
    localStorage.setItem('contactedClients', JSON.stringify(updated));
  };

  const isClientContacted = (clientId) => {
    return contactedClients[clientId] === true;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copiado al portapapeles');
  };

  const openPaymentModal = (client) => {
    setPaymentModal({ show: true, client });
  };

  const registerPayment = async (client) => {
    if (!client) return;
    
    try {
      setProcessingPayment(client._id);
      
      const response = await axios.post('/api/mikrotik/register-payment', {
        clientId: client.mikrowispId,
        amount: parseFloat(client.deuda),
        paymentMethod: 'EFECTIVO'
      });

      if (response.data.success) {
        alert(`Pago registrado exitosamente para ${client.nombre}`);
        setPaymentModal({ show: false, client: null });
        await loadSuspendedClients();
      } else {
        alert(`Error: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error registering payment:', error);
      alert(`Error al registrar el pago: ${error.response?.data?.message || error.message}`);
    } finally {
      setProcessingPayment(null);
    }
  };

  const filteredClients = suspendedClients.filter(client => {
    const searchLower = searchTerm.toLowerCase();
    return (
      client.nombre?.toLowerCase().includes(searchLower) ||
      client.id?.toString().includes(searchTerm) ||
      client.cedula?.includes(searchTerm) ||
      client.telefono?.includes(searchTerm) ||
      client.direccion?.toLowerCase().includes(searchLower)
    );
  });

  // Si no est? autenticado, mostrar login
  if (!isAuthenticated) {
    return <SuspendedClientsLogin onLoginSuccess={handleLoginSuccess} />;
  }

  if (loading && !lastUpdate) {
    return (
      <div className="suspended-panel">
        <div className="suspended-loading">
          <div className="spinner"></div>
          <p>Cargando clientes suspendidos de Mikrotik...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="suspended-panel">
      <div className="suspended-header">
        <div className="header-title">
          <h2>
            <XCircle size={24} style={{display: 'inline', marginRight: '8px'}} /> 
            Clientes Suspendidos - Mikrotik
            <span className="total-badge">{suspendedClients.length}</span>
          </h2>
          {lastUpdate && (
            <span className="last-update">
              ?ltima actualizaci?n: {formatDate(lastUpdate)}
              <span className="auto-update-info"> (Se actualiza autom?ticamente cada 12 horas)</span>
            </span>
          )}
        </div>
        <button 
          onClick={forceRefresh} 
          className="refresh-button"
          disabled={loading}
          title="Forzar actualizaci?n desde Mikrowisp"
        >
          {loading ? 'Actualizando...' : 'Actualizar Ahora'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon"><AlertTriangle size={20} /></span>
          <span>{error}</span>
          <button onClick={loadSuspendedClients} className="retry-button">
            Reintentar
          </button>
        </div>
      )}

      <div className="suspended-search">
        <div className="search-input-container">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder="Buscar por nombre, ID, c?dula, tel?fono o direcci?n..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="results-count">
          {filteredClients.length} de {suspendedClients.length} clientes
        </div>
      </div>

      <div className="suspended-content">
        {filteredClients.length === 0 ? (
          <div className="empty-state">
            {searchTerm ? (
              <p>No se encontraron clientes que coincidan con "{searchTerm}"</p>
            ) : (
              <p><CheckCircle size={24} style={{display: 'inline', marginRight: '8px', color: '#4CAF50'}} /> No hay clientes suspendidos en este momento</p>
            )}
          </div>
        ) : (
          <div className="clients-grid">
            {filteredClients.map((client) => (
              <div key={client.id} className="client-card">
                <div className="client-header">
                  <div className="client-info">
                    <h3>{client.nombre || 'Sin nombre'}</h3>
                    <span className="client-id">ID: {client.id}</span>
                  </div>
                  <div className="header-actions">
                    <button
                      onClick={() => verifyClientStatus(client)}
                      className="verify-button"
                      disabled={verifyingClient === client.id}
                      title="Verificar estado actual en Mikrowisp"
                    >
                      <RefreshCw size={14} className={verifyingClient === client.id ? 'spinning' : ''} />
                    </button>
                    <span className="status-badge suspended">
                      <XCircle size={14} style={{display: 'inline', marginRight: '4px'}} /> Suspendido
                    </span>
                    <button
                      onClick={() => markAsContacted(client._id || client.id)}
                      className={`contacted-chip ${isClientContacted(client._id || client.id) ? 'contacted' : ''}`}
                      title={isClientContacted(client._id || client.id) ? 'Ya marcado como contactado' : 'Marcar como contactado'}
                    >
                      <Check size={14} />
                      {isClientContacted(client._id || client.id) ? 'Ya me comuniqu?' : 'Marcar contactado'}
                    </button>
                  </div>
                </div>

                <div className="client-body">
                  {client.cedula && (
                    <div className="info-row">
                      <span className="info-label"><CreditCard size={16} /> C?dula:</span>
                      <span className="info-value">{client.cedula}</span>
                    </div>
                  )}

                  {client.telefono && (
                    <div className="info-row">
                      <span className="info-label"><Phone size={16} /> Tel?fono:</span>
                      <span className="info-value">
                        {client.telefono}
                        <button
                          onClick={() => openWhatsApp(client.telefono)}
                          className="whatsapp-btn"
                          title="Abrir en WhatsApp"
                        >
                          <MessageCircle size={14} />
                        </button>
                        <button
                          onClick={() => copyToClipboard(client.telefono)}
                          className="copy-btn"
                          title="Copiar tel?fono"
                        >
                          <Copy size={14} />
                        </button>
                      </span>
                    </div>
                  )}

                  {client.email && (
                    <div className="info-row">
                      <span className="info-label"><Mail size={16} /> Email:</span>
                      <span className="info-value">{client.email}</span>
                    </div>
                  )}

                  {client.direccion && (
                    <div className="info-row">
                      <span className="info-label"><MapPin size={16} /> Direcci?n:</span>
                      <span className="info-value">{client.direccion}</span>
                    </div>
                  )}

                  {client.plan && (
                    <div className="info-row">
                      <span className="info-label"><Package size={16} /> Plan:</span>
                      <span className="info-value">{client.plan}</span>
                    </div>
                  )}

                  {client.deuda && parseFloat(client.deuda) > 0 && (
                    <div className="info-row">
                      <span className="info-label"><DollarSign size={16} /> Deuda:</span>
                      <span className="info-value debt-amount">
                        RD$ {parseFloat(client.deuda).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                  
                  {client.facturas_pendientes > 0 && (
                    <div className="info-row">
                      <span className="info-label"><FileText size={16} /> Facturas Pendientes:</span>
                      <span className="info-value">{client.facturas_pendientes}</span>
                    </div>
                  )}

                  {client.fecha_suspension && (
                    <div className="info-row">
                      <span className="info-label"><Calendar size={16} /> Suspendido:</span>
                      <span className="info-value">{formatDate(client.fecha_suspension)}</span>
                    </div>
                  )}

                  {client.fecha_ultimo_pago && client.fecha_ultimo_pago !== '0000-00-00' && client.fecha_ultimo_pago !== '0000-00-00 00:00:00' && (
                    <div className="info-row">
                      <span className="info-label"><CreditCard size={16} /> ?ltimo Pago:</span>
                      <span className="info-value">{formatDate(client.fecha_ultimo_pago)}</span>
                    </div>
                  )}

                  {/* Bot?n de pagar */}
                  {client.deuda && parseFloat(client.deuda) > 0 && (
                    <div className="payment-button-container">
                      <button
                        className="payment-button"
                        onClick={() => handlePaymentClick(client)}
                        disabled={processingPayment === client.id}
                      >
                        {processingPayment === client.id ? (
                          <><DollarSign size={18} /> Procesando...</>
                        ) : (
                          <><DollarSign size={18} /> Pagar en Efectivo</>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal de confirmaci?n de pago */}
        {paymentModal.show && paymentModal.client && (
          <div className="payment-modal-overlay" onClick={cancelPayment}>
            <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
              <h3><AlertTriangle size={24} style={{display: 'inline', marginRight: '8px'}} /> CONFIRMAR REGISTRO DE PAGO</h3>
              <div className="modal-content">
                <div className="warning-message">
                  <p className="warning-title"><AlertTriangle size={20} style={{display: 'inline', marginRight: '8px'}} /> IMPORTANTE: Esta acci?n registrar? el pago en Mikrowisp</p>
                  <p className="warning-subtitle">?Est? seguro que desea continuar?</p>
                </div>
                
                <div className="payment-details">
                  <div className="detail-row">
                    <span className="detail-label"><User size={16} /> Cliente:</span>
                    <span className="detail-value">{paymentModal.client.nombre}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label"><CreditCard size={16} /> C?dula:</span>
                    <span className="detail-value">{paymentModal.client.cedula || 'N/A'}</span>
                  </div>
                  <div className="detail-row highlight">
                    <span className="detail-label"><DollarSign size={16} /> Monto a registrar:</span>
                    <span className="detail-value amount">RD$ {parseFloat(paymentModal.client.deuda).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label"><DollarSign size={16} /> Forma de pago:</span>
                    <span className="detail-value">EFECTIVO</span>
                  </div>
                </div>

                <p className="warning-text"><AlertTriangle size={16} style={{display: 'inline', marginRight: '8px'}} /> ADVERTENCIA: Este pago se registrar? inmediatamente en el sistema Mikrowisp y no se puede deshacer desde este panel.</p>
              </div>
              <div className="modal-buttons">
                <button className="btn-confirm" onClick={confirmPayment}>
                  <CheckCircle size={18} style={{display: 'inline', marginRight: '6px'}} /> SI, REGISTRAR PAGO
                </button>
                <button className="btn-cancel" onClick={cancelPayment}>
                  <XCircle size={18} style={{display: 'inline', marginRight: '6px'}} /> NO, CANCELAR
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SuspendedClientsPanel;
